import React, { useCallback, useRef, useEffect, memo } from 'react';
import { Search, Loader2 } from 'lucide-react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  isLoading?: boolean;
}

const SearchInput = memo(({
  value,
  onChange,
  placeholder = "Search...",
  label,
  isLoading = false
}: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFocusRef = useRef(false);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  // Track focus state
  const handleFocus = useCallback(() => {
    hasFocusRef.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    hasFocusRef.current = false;
  }, []);

  // Restore focus after re-renders
  useEffect(() => {
    if (hasFocusRef.current && inputRef.current) {
      const input = inputRef.current;
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        input.focus();
        // Restore cursor position
        const len = value.length;
        input.setSelectionRange(len, len);
      });
    }
  });

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className="relative">
        {isLoading ? (
          <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        )}
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          autoComplete="off"
        />
      </div>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput; 